import {
  Box,
  FormHelperText,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type IProps = TextFieldProps & {
  name: string;
  label: string;
};

export const TwoFactorAuthInput: React.FC<IProps> = ({
  name,
  label,
  ...rest
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>(
    Array(6).fill(null)
  );

  const fieldError = errors[name];
  const errorMessage = fieldError?.message as string | undefined;

  return (
    <Box
      sx={{
        marginBottom: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Typography
          component='label'
          variant='h5'
          sx={{
            display: 'block',
            marginBottom: 1,
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
      </Box>

      <Controller
        name={name}
        control={control}
        defaultValue=''
        rules={{
          required: 'Dieses Feld ist erforderlich',
          pattern: {
            value: /^\d{6}$/,
            message:
              'Bitte alle 6 Ziffern des Authentifizierungsschlüssel eingeben',
          },
        }}
        render={({ field }) => {
          // Parse the current field value to an array of single digits
          const codeArray = Array(6).fill('');
          const fieldValue = field.value ? field.value.toString() : '';

          // Fill the codeArray with actual values
          for (let i = 0; i < fieldValue.length && i < 6; i++) {
            codeArray[i] = fieldValue[i];
          }

          const handleChange = (index: number, newValue: string) => {
            const digit = newValue.slice(-1);

            if (/^\d?$/.test(digit)) {
              const newCode = [...codeArray];
              newCode[index] = digit;
              field.onChange(newCode.join(''));

              if (digit && index < 5) {
                inputRefs.current[index + 1]?.focus();
              }
            }
          };

          const handleKeyDown = (
            index: number,
            e: React.KeyboardEvent<HTMLDivElement>
          ) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              if (codeArray[index] !== '') {
                const newCode = [...codeArray];
                newCode[index] = '';
                field.onChange(newCode.join(''));
              } else if (index > 0) {
                inputRefs.current[index - 1]?.focus();
              }
            }
          };

          const handlePaste = (
            event: React.ClipboardEvent<HTMLInputElement>
          ) => {
            event.preventDefault();
            const index = 0;
            const pastedText = event.clipboardData?.getData('text') || '';
            const digits = pastedText
              .replace(/\D/g, '')
              .split('')
              .slice(0, 6 - index);

            const newCode = [...codeArray];
            let fillIndex = index;

            digits.forEach(digit => {
              if (fillIndex < 6) {
                newCode[fillIndex] = digit;
                fillIndex++;
              }
            });

            field.onChange(newCode.join(''));
            let nextFocus = fillIndex;

            while (nextFocus < 6 && newCode[nextFocus] !== '') {
              nextFocus++;
            }

            if (nextFocus < 6) {
              inputRefs.current[nextFocus]?.focus();
            } else {
              inputRefs.current[5]?.focus();
            }
          };

          return (
            <>
              <div style={{ display: 'flex', gap: '8px' }}>
                {codeArray.map((digit, i) => (
                  <TextField
                    key={i}
                    inputRef={el => (inputRefs.current[i] = el)}
                    value={digit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleChange(i, e.target.value);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
                      handleKeyDown(i, e)
                    }
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                      handlePaste(e as React.ClipboardEvent<HTMLInputElement>)
                    }
                    inputProps={{
                      pattern: '[0-9]*',
                      style: { textAlign: 'center', fontSize: '1.25rem' },
                    }}
                    variant='outlined'
                    size='small'
                    style={{ width: '48px', height: '48px' }}
                    error={!!fieldError}
                    {...rest}
                  />
                ))}
              </div>
              {errorMessage && (
                <FormHelperText error sx={{ marginLeft: 1, marginTop: 0.5 }}>
                  {errorMessage}
                </FormHelperText>
              )}
            </>
          );
        }}
      />
    </Box>
  );
};
